import { useState, useCallback } from "react";

/**
 * Manages timeframe + custom date range selection state.
 *
 * @param {string} defaultTimeframe - Initial preset (e.g. "thisMonth", "thisWeek")
 * @param {{ onReset?: () => void }} options
 *   onReset — called on every transition so the parent can close drawers/drilldowns
 *
 * @returns {{
 *   timeframe: string,
 *   dateRange: { start: string|null, end: string|null },
 *   startDate: string|null,
 *   endDate: string|null,
 *   isRangeActive: boolean,
 *   onTimeframeChange: (value: string) => void,
 *   onRangeChange: ({ start: string, end: string }) => void,
 *   onRangeClear: () => void,
 * }}
 */
export function useTimeframeSelector(defaultTimeframe, { onReset } = {}) {
    const [timeframe, setTimeframe] = useState(defaultTimeframe);
    const [dateRange, setDateRange] = useState({ start: null, end: null });

    const onTimeframeChange = useCallback((value) => {
        setTimeframe(value);
        setDateRange({ start: null, end: null });
        onReset?.();
    }, [onReset]);

    const onRangeChange = useCallback(({ start, end }) => {
        setTimeframe("range");
        setDateRange({ start, end });
        onReset?.();
    }, [onReset]);

    const onRangeClear = useCallback(() => {
        setTimeframe(defaultTimeframe);
        setDateRange({ start: null, end: null });
    }, [defaultTimeframe]);

    const isRangeActive = timeframe === "range" && Boolean(dateRange.start && dateRange.end);

    return {
        timeframe,
        dateRange,
        startDate: isRangeActive ? dateRange.start : null,
        endDate: isRangeActive ? dateRange.end : null,
        isRangeActive,
        onTimeframeChange,
        onRangeChange,
        onRangeClear,
    };
}
